import React, { useCallback, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { Badge, makeStyles, Typography } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Fade from '@material-ui/core/Fade';
import {
  alert as alertAction,
  close as closeAlertAction
} from '../../features/alertSystem/actions';
import { uploadImage as uploadImageAction } from '../../features/imageSystem/actions';
import { getLanguage } from 'features/app/selectors';
const useStyles = makeStyles(theme => ({
  reject: {
    background: theme.palette.grey[300],
    transform: 'scale(1.1)'
  },
  accept: {
    background: theme.palette.grey[300],
    transform: 'scale(1.1)'
  },
  root: {
    height: '100%',
    transition: 'all .1s ease-in-out'
  },
  progressContainer: {
    width: '100%',
    position: 'absolute'
  },
  progressRoot: {
    width: '100%',
    height: '3px'
  }
}));

const MAX_FILE_SIZE = 5242880;
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];

const ImageDropzone = ({
  thumbnailHeight,
  thumbnailWidth,
  multiple = false,
  onUploadImage,
  // If true, disables native file selection dialog
  noClick = false,
  uploadImage,
  alert,
  closeAlert,
  // Content to show in the dropzone, e.g. image if one exists
  children,
  action,
  isUsed,
  logoUseIn
}) => {
  const classes = useStyles();
  const [isUploading, setIsUploading] = useState(false);
  const language = useSelector(getLanguage);
  //const spacedLogoUseIn = isUsed && logoUseIn.replace(/,/g, ', ');

  const uploadFiles = useCallback(
    acceptedFiles => {
      function onUploadSuccess(data) {
        console.log('onUploadSuccess', data);
        onUploadImage(data.image);
        setIsUploading(false);
      }

      function onUploadError() {
        setIsUploading(false);
      }

      if (acceptedFiles.length > 0) {
        setIsUploading(true);
      }

      acceptedFiles.forEach(file => {
        console.log('About to upload file', file);
        uploadImage({
          file,
          thumbnailHeight,
          thumbnailWidth,
          action,
          options: {
            alert,
            closeAlert,
            onSuccess: onUploadSuccess,
            onError: onUploadError
          }
        });
      });
    },
    [
      uploadImage,
      thumbnailHeight,
      thumbnailWidth,
      onUploadImage,
      alert,
      closeAlert
    ]
  );

  const notifyRejectedFiles = useCallback(
    rejectedFiles => {
      rejectedFiles.forEach((file = {}) => {
        if (ACCEPTED_FILE_TYPES.indexOf(file.type) === -1) {
          alert({
            title: <FormattedMessage id="notify.uploadImageFail" />,
            content: (
              <FormattedMessage
                id="media.wrongImageFormat"
                values={{ br: <br /> }}
              />
            ),
            actions: {
              right: [
                <Button key="stay" onClick={closeAlert} color="primary">
                  <FormattedMessage id="notify.alertOk" />
                </Button>
              ]
            }
          });
        } else if (file.size > MAX_FILE_SIZE) {
          alert({
            title: <FormattedMessage id="notify.uploadImageFail" />,
            content: <FormattedMessage id="media.wrongImageSize" />,
            actions: {
              right: [
                <Button key="stay" onClick={closeAlert} color="primary">
                  <FormattedMessage id="notify.alertOk" />
                </Button>
              ]
            }
          });
        }
      });
    },
    [alert, closeAlert]
  );

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      notifyRejectedFiles(rejectedFiles);
      if (isUsed) {
        alert({
          label: 'ppi.approvalVersionCommentLabel',
          title: (
            <>
              <Badge badgeContent={1} color="primary"></Badge>
              <span style={{ fontSize: '14px', marginLeft: '20px' }}>
                <FormattedMessage
                  size="14px"
                  id="ppi.publishAffectedPpsisLabel"
                />
              </span>
            </>
          ),
          content: (
            <>
              <FormattedMessage id="ppi.dishLabelWarning" />
              <br />
              <br />
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                <FormattedMessage
                  size="14px"
                  id="Affected Dish Instructions :"
                />
              </span>
              <br />
              {logoUseIn &&
                logoUseIn.map(item => {
                  return (
                    <div>
                      <FormattedMessage
                        id={item.info[language].presentationName}
                      />{' '}
                      - <FormattedMessage id={item.formulaCode} />
                    </div>
                  );
                })}
              <br />
              <br />
              <FormattedMessage id="ppi.dishLabelNote" />
            </>
          ),
          actions: {
            right: (
              <>
                <Button key="cancel" onClick={closeAlert} color="primary">
                  <FormattedMessage id="ppi.confirmCreateDraftInUseCancel" />
                </Button>
                <Button
                  key="ok"
                  onClick={() => {
                    uploadFiles(acceptedFiles);
                    closeAlert();
                  }}
                  color="primary"
                  variant="contained"
                >
                  <FormattedMessage id="ppi.confirmCreateDraftInUseOk" />
                </Button>
              </>
            )
          }
        });
      } else {
        uploadFiles(acceptedFiles);
      }
    },
    [notifyRejectedFiles, uploadFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_SIZE,
    accept: ACCEPTED_FILE_TYPES,
    multiple,
    noClick
  });

  const rootClassName = cx(
    classes.root,
    { [classes.accept]: isDragAccept },
    { [classes.reject]: isDragReject }
  );

  return (
    <div {...getRootProps({ className: rootClassName })}>
      <input {...getInputProps()} />
      <Fade in={isUploading}>
        <div className={classes.progressContainer}>
          <LinearProgress
            color="secondary"
            classes={{ root: classes.progressRoot }}
          />
        </div>
      </Fade>
      {children}
    </div>
  );
};

export default connect(
  null,
  {
    uploadImage: uploadImageAction,
    alert: alertAction,
    closeAlert: closeAlertAction
  }
)(ImageDropzone);
